import { Link } from "react-router-dom";
import { ArrowRight, Cloud, GitBranch, ShieldCheck, Linkedin, Mail } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import XLogo from "@/components/XLogo";
import { Reveal, FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { ACADEMY_IMAGE, ACADEMY_DISCIPLINES, SOCIAL_LINKS, CONTACT_EMAIL, mailto } from "@/data/site";

const DISCIPLINE_ICONS = { "Cloud Computing": Cloud, DevOps: GitBranch, Cybersecurity: ShieldCheck };

const Academy = () => (
  <div data-testid="academy-page">
    <Seo
      title="TecKube Academy — Cloud, DevOps & Cybersecurity Training"
      description="TecKube Academy delivers training in Cloud Computing, DevOps and Cybersecurity. Upcoming training dates are announced on TecKube's social channels."
    />
    <PageHero
      eyebrow="TecKube Academy"
      title="Sharing knowledge in Cloud Computing, DevOps and Cybersecurity."
      lead="TecKube Academy delivered training across these three technical disciplines."
    />

    <section className="bg-paper pb-24 md:pb-32" data-testid="academy-disciplines">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-5 md:grid-cols-3">
          {ACADEMY_DISCIPLINES.map((discipline, i) => {
            const Icon = DISCIPLINE_ICONS[discipline];
            return (
              <Reveal key={discipline} delay={i * 0.08}>
                <div
                  className="spotlight group flex h-full min-h-[280px] flex-col justify-between rounded-3xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)] md:p-10"
                  data-testid={`academy-discipline-card-${i + 1}`}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                  }}
                >
                  <div>
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </span>
                    <span className="mt-6 block font-mono text-xs tracking-[0.25em] text-primary">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-ink">
                      {discipline}
                    </h2>
                  </div>
                  <p className="mt-6 text-[15px] leading-relaxed text-soot">
                    One of the three technical disciplines the Academy delivered training across.
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-20 grid items-center gap-12 md:mt-28 md:grid-cols-12">
          <div className="md:col-span-5">
            <Reveal>
              <div className="group overflow-hidden rounded-3xl border border-slate-200 shadow-[0_30px_70px_-36px_rgba(15,23,42,0.35)]">
                <img
                  src={ACADEMY_IMAGE}
                  alt="TecKube Academy training"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={0.1}>
              <span className="eyebrow">Our history</span>
              <h2 className="mt-7 font-display text-[clamp(1.7rem,3vw,2.6rem)] font-semibold leading-[1.1] tracking-tight text-ink">
                Part of TecKube&rsquo;s history of combining technology services with technical
                education.
              </h2>
              <p className="mt-7 text-lg leading-relaxed text-soot">
                The Academy forms part of TecKube&rsquo;s history of combining technology services
                with technical education — the same knowledge that shapes how we build, deploy and
                secure for clients.
              </p>
              <Link
                to="/about"
                data-testid="academy-about-link"
                className="group mt-9 inline-flex items-center gap-2 font-display text-[15px] font-semibold text-primary"
              >
                More about TecKube
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section className="noise relative overflow-hidden bg-night py-24 md:py-32" data-testid="academy-announcements">
      <div
        aria-hidden="true"
        className="absolute -right-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/20 blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="eyebrow-dark">Training announcements</span>
              <h2 className="mt-7 max-w-2xl font-display text-[clamp(1.9rem,3.4vw,3rem)] font-semibold leading-[1.06] tracking-tight text-white">
                The next training starts soon — watch our social channels.
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/65">
                We don&rsquo;t publish course dates on the website. When the next TecKube Academy
                training is starting, we announce it first on LinkedIn and X — follow us there so
                you don&rsquo;t miss it.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`academy-${s.id}-link`}
                    className="btn btn-light-outline"
                  >
                    {s.id === "linkedin" ? <Linkedin className="h-4 w-4" /> : <XLogo className="h-3 w-3" />}
                    Follow on {s.id === "linkedin" ? "LinkedIn" : "X"}
                  </a>
                ))}
                <a
                  href={mailto("TecKube Academy enquiry")}
                  data-testid="academy-email-link"
                  className="inline-flex items-center gap-2 font-mono text-sm text-white/70 underline decoration-white/25 underline-offset-4 transition-colors duration-300 hover:text-white"
                >
                  <Mail className="h-4 w-4" />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <FadeIn delay={0.15}>
              <div
                className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm"
                data-testid="academy-questions-card"
              >
                <p className="font-display text-lg font-semibold text-white">Questions?</p>
                <p className="mt-3 text-[15px] leading-relaxed text-white/65">
                  Email us with any questions about the Academy or upcoming training.
                </p>
                <a
                  href={mailto("TecKube Academy — question")}
                  data-testid="academy-questions-mailto-button"
                  className="mt-6 inline-flex items-center gap-2 font-display text-[15px] font-semibold text-white"
                >
                  Email the Academy
                  <ArrowRight className="h-4 w-4 text-primary-soft" />
                </a>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>

    <CTABand />
  </div>
);

export default Academy;
